import { useEffect, useRef, useState } from "react";
import { useLang } from "@/lib/i18n";
import { MessageCircle, X, Send, Sparkles, Phone } from "lucide-react";
import { API } from "@/lib/api";

export default function ChatWidget({ propertySlug, propertyName, propertyPhone = "" }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(`s_${Math.random().toString(36).slice(2)}_${Date.now()}`);

  // Sanitize phone to E.164-ish digits for wa.me
  const waNumber = (propertyPhone || "").replace(/[^\d]/g, "");
  const lastMsg = messages[messages.length - 1]?.content || "";
  const waText = encodeURIComponent(
    (lang === "it" ? "Ciao! Ti scrivo dal sito HospitalityHub. Avevo chiesto: "
                   : "Hi! Writing from HospitalityHub. I was asking: ") +
    (messages.filter((m) => m.role === "user").slice(-1)[0]?.content || "")
  );
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${waText}` : null;

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: lang === "it"
        ? `Ciao! Sono l'assistente di ${propertyName}. Come posso aiutarti?`
        : `Hi! I'm the ${propertyName} assistant. How can I help?` }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const q = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ property_slug: propertySlug, session_id: sessionId.current, message: q, lang }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            const idx = copy.length - 1;
            if (copy[idx]?.role === "assistant") {
              copy[idx] = { ...copy[idx], content: copy[idx].content + line };
            }
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: lang === "it" ? "Errore nel servizio AI." : "AI service error." };
        return copy;
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl grid place-items-center lift hover:scale-110 transition-transform"
      >
        {open ? <X className="w-6 h-6"/> : <MessageCircle className="w-6 h-6"/>}
      </button>
      {open && (
        <div
          data-testid="chat-widget"
          className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-[400px] h-[520px] bg-card border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="bg-primary text-primary-foreground p-4 flex items-center gap-3">
            <Sparkles className="w-5 h-5"/>
            <div>
              <div className="font-display text-xl leading-none">{propertyName}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 mt-1">
                {lang === "it" ? "Assistente virtuale" : "Virtual assistant"}
              </div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={`${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-sand text-ink border border-border"}`}>
                  {m.content || (busy ? <span className="inline-block w-2 h-2 rounded-full bg-primary/60 animate-pulse"/> : "")}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="border-t border-border p-3 flex gap-2 bg-background">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={lang === "it" ? "Scrivi un messaggio..." : "Type a message..."}
              className="flex-1 bg-transparent px-3 py-2 rounded-md border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="chat-input"
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()} className="w-10 h-10 rounded-md bg-primary text-primary-foreground grid place-items-center lift disabled:opacity-50" data-testid="chat-send-btn">
              <Send className="w-4 h-4"/>
            </button>
          </form>
          {waHref && (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="border-t border-border bg-sand px-4 py-2.5 text-xs flex items-center justify-center gap-2 text-ink hover:bg-primary/5 transition-colors"
              data-testid="chat-whatsapp-btn"
            >
              <Phone className="w-3.5 h-3.5 text-sage"/>
              <span>{lang === "it" ? "Preferisci parlare con la struttura?" : "Prefer to talk to the host?"}</span>
              <b className="text-sage">WhatsApp →</b>
            </a>
          )}
        </div>
      )}
    </>
  );
}
